import { useState } from 'react';

/** How long sub-rows take to collapse and to grow in. */
const CLOSE_MS = 150;
const OPEN_MS = 200;

function without(set: ReadonlySet<string>, name: string): ReadonlySet<string> {
  const next = new Set(set);
  next.delete(name);
  return next;
}

/** Which rows have their sub-rows open, and which are collapsing; every row starts closed. */
export function useExpandedRows() {
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());
  const [closing, setClosing] = useState<ReadonlySet<string>>(new Set());
  const toggle = (name: string) => {
    if (!open.has(name)) {
      setClosing((current) => without(current, name));
      setOpen((current) => new Set(current).add(name));
      return;
    }
    setOpen((current) => without(current, name));
    setClosing((current) => new Set(current).add(name));
    // A little past the animation, which starts at the next commit, so the row is gone only once it's flat.
    setTimeout(() => setClosing((current) => without(current, name)), CLOSE_MS + 50);
  };
  return { isExpanded: (name: string) => open.has(name), isClosing: (name: string) => closing.has(name), toggle };
}

/**
 * Grows a row's cells from nothing to their height, or shrinks them back. Table rows can't
 * animate height in CSS, so each cell and its content box is animated to its measured height.
 */
function animateRow(row: HTMLTableRowElement | null, opening: boolean) {
  if (!row?.animate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  // Opening drops the animation at the end so the row is styled as normal again; closing holds it until unmount.
  const timing: KeyframeAnimationOptions = { duration: opening ? OPEN_MS : CLOSE_MS, easing: opening ? 'ease-out' : 'ease-in', fill: 'forwards' };
  // Measure without a running animation: a reopen mid-close, or StrictMode's second ref call, would read a flat row.
  for (const cell of row.cells) for (const animation of cell.getAnimations({ subtree: true })) animation.cancel();
  const play = (element: HTMLElement, shut: Keyframe, full: Keyframe) => {
    const animation = element.animate(opening ? [shut, full] : [full, shut], timing);
    if (opening) animation.finished.then(() => animation.cancel(), () => {});
  };
  for (const cell of row.cells) {
    const style = getComputedStyle(cell);
    play(
      cell,
      { height: '0px', paddingTop: '0px', paddingBottom: '0px', borderBottomWidth: '0px' },
      { height: `${cell.offsetHeight}px`, paddingTop: style.paddingTop, paddingBottom: style.paddingBottom, borderBottomWidth: style.borderBottomWidth },
    );
    const content = cell.firstElementChild;
    if (content instanceof HTMLElement) {
      play(
        content,
        { maxHeight: '0px', minHeight: '0px', overflow: 'hidden', opacity: 0 },
        { maxHeight: `${content.scrollHeight}px`, minHeight: '0px', overflow: 'hidden', opacity: 1 },
      );
    }
  }
}

const expandRow = (row: HTMLTableRowElement | null) => animateRow(row, true);
const collapseRow = (row: HTMLTableRowElement | null) => animateRow(row, false);

/** Props for a sub-row's <tr>: it grows in when its category opens and shrinks out when it closes. */
export function subRowProps(closing: boolean) {
  return closing ? { ref: collapseRow, 'aria-hidden': true } : { ref: expandRow };
}
